/**
 * PolygonOps - 2D boolean operations on polygon rings
 *
 * Wraps martinez-polygon-clipping to subtract slot cutouts from part
 * outlines before extrusion. This is what turns visual-only slots into
 * real cuts in the fabricated geometry (waffle joints).
 *
 * Conventions:
 * - A "ring" is an open Array<{x, y}> (no repeated end point).
 * - A result polygon is { outer: ring, holes: Array<ring> }.
 */

import * as martinez from 'martinez-polygon-clipping';

/**
 * Signed area of a ring (positive = counter-clockwise)
 * @param {Array<{x,y}>} ring
 * @returns {number}
 */
export function signedArea(ring) {
    let area = 0;
    for (let i = 0; i < ring.length; i++) {
        const p1 = ring[i];
        const p2 = ring[(i + 1) % ring.length];
        area += (p1.x * p2.y) - (p2.x * p1.y);
    }
    return area / 2;
}

/**
 * Ensure a ring has the requested winding order
 * @param {Array<{x,y}>} ring
 * @param {boolean} ccw - true for counter-clockwise
 * @returns {Array<{x,y}>} same ring or a reversed copy
 */
export function ensureWinding(ring, ccw = true) {
    const isCCW = signedArea(ring) > 0;
    return (isCCW === ccw) ? ring : ring.slice().reverse();
}

/**
 * Convert a ring to martinez coordinate format (closed: first point repeated).
 * Consecutive near-duplicate vertices are dropped - degenerate zero-length
 * edges are a known infinite-loop trigger inside martinez.
 * @param {Array<{x,y}>} ring
 * @returns {Array<[number,number]>}
 */
function ringToCoords(ring) {
    const EPS = 1e-9;
    const coords = [];

    for (const p of ring) {
        if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
        const prev = coords[coords.length - 1];
        if (prev && Math.abs(prev[0] - p.x) < EPS && Math.abs(prev[1] - p.y) < EPS) continue;
        coords.push([p.x, p.y]);
    }

    if (coords.length > 1) {
        // Drop a duplicated closing point before re-closing explicitly
        const first = coords[0];
        const last = coords[coords.length - 1];
        if (Math.abs(first[0] - last[0]) < EPS && Math.abs(first[1] - last[1]) < EPS) {
            coords.pop();
        }
    }
    if (coords.length > 0) {
        coords.push([coords[0][0], coords[0][1]]);
    }
    return coords;
}

/**
 * Convert martinez coordinates back to an open ring
 * @param {Array<[number,number]>} coords
 * @returns {Array<{x,y}>}
 */
function coordsToRing(coords) {
    const ring = coords.map(c => ({ x: c[0], y: c[1] }));
    // Strip repeated closing point
    if (ring.length > 1) {
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (Math.abs(first.x - last.x) < 1e-9 && Math.abs(first.y - last.y) < 1e-9) {
            ring.pop();
        }
    }
    return ring;
}

/**
 * Sample a closed shape's points into a polyline ring, subdividing cubic
 * bezier segments. This is THE shared sampler for fabrication outlines:
 * mesh generation (Application.generateMeshesForPlane) and part collection
 * (SVGExporter.collectParts) both use it, so the 3D panels and the exported
 * cut patterns are guaranteed to agree on the sampled outline (and on any
 * union of overlapping strokes computed from it).
 *
 * @param {Array<{x,y,controlPoint1?,controlPoint2?}>} points - Closed shape
 *        points (SketchPoints or plain objects)
 * @param {number} [steps=16] - Subdivisions per bezier segment
 * @returns {Array<{x,y}>} Open sampled ring
 */
export function sampleRing(points, steps = 16) {
    const out = [];
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const cur = points[i];
        const next = points[(i + 1) % n];
        out.push({ x: cur.x, y: cur.y });

        const c1 = cur.controlPoint2;
        const c2 = next.controlPoint1;
        if (c1 || c2) {
            const p1 = c1 || cur;
            const p2 = c2 || next;
            for (let s = 1; s < steps; s++) {
                const t = s / steps;
                const t1 = 1 - t;
                out.push({
                    x: t1 * t1 * t1 * cur.x + 3 * t1 * t1 * t * p1.x + 3 * t1 * t * t * p2.x + t * t * t * next.x,
                    y: t1 * t1 * t1 * cur.y + 3 * t1 * t1 * t * p1.y + 3 * t1 * t * t * p2.y + t * t * t * next.y
                });
            }
        }
    }
    return out;
}

/**
 * Test whether a closed ring self-intersects (any two non-adjacent edges cross)
 * @param {Array<{x,y}>} ring - Open ring
 * @returns {boolean}
 */
export function ringSelfIntersects(ring) {
    const n = ring.length;
    if (n < 4) return false;

    const ccw = (p, q, r) => (r.y - p.y) * (q.x - p.x) - (q.y - p.y) * (r.x - p.x);

    for (let i = 0; i < n; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % n];
        for (let j = i + 2; j < n; j++) {
            if (i === 0 && j === n - 1) continue; // adjacent through wrap
            const c = ring[j];
            const d = ring[(j + 1) % n];
            const d1 = ccw(c, d, a), d2 = ccw(c, d, b);
            const d3 = ccw(a, b, c), d4 = ccw(a, b, d);
            if (((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0))) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Union a list of SIMPLE rings (convex quads, discs, ...) into one polygon,
 * returning the largest result polygon WITH its holes. Rings are folded
 * pairwise so overlapping inputs are always well-defined (a
 * self-intersecting multipolygon operand is not).
 *
 * Holes matter: a closed brush stroke (a drawn ring/donut) unions into an
 * annulus - dropping the hole would silently turn the part into a disc.
 *
 * @param {Array<Array<{x,y}>>} rings - Simple open rings
 * @returns {{outer: Array<{x,y}>, holes: Array<Array<{x,y}>>}|null}
 *          Largest union polygon, or null on failure
 */
export function unionRingsToPolygon(rings) {
    const valid = (rings || []).filter(r => r && r.length >= 3);
    if (valid.length === 0) return null;

    // Deterministic sub-micron jitter: axis-aligned strokes produce EXACT
    // tangencies (quad edges touching discs) and collinear overlaps, which
    // can send martinez into an infinite loop. ~1e-4 px is 0.1 microns at
    // chair scale - invisible, but breaks the degeneracies. (1e-6 was
    // below martinez float tolerance for vertex-on-edge cases.)
    let seed = 0;
    const jitter = () => {
        seed = (seed * 1664525 + 1013904223) >>> 0;
        return ((seed / 4294967296) - 0.5) * 2e-4;
    };
    const jittered = valid.map(ring => ring.map(pt => ({
        x: pt.x + jitter(),
        y: pt.y + jitter()
    })));

    try {
        let result = [[ringToCoords(jittered[0])]]; // MultiPolygon
        for (let i = 1; i < jittered.length; i++) {
            result = martinez.union(result, [ringToCoords(jittered[i])]);
            if (!result || result.length === 0) return null;
        }

        let best = null;
        let bestArea = 0;
        for (const poly of result) {
            if (!poly || poly.length === 0) continue;
            const outer = coordsToRing(poly[0]);
            const area = Math.abs(signedArea(outer));
            if (area > bestArea) {
                bestArea = area;
                const holes = [];
                for (let h = 1; h < poly.length; h++) {
                    const hole = coordsToRing(poly[h]);
                    if (hole.length >= 3) holes.push(hole);
                }
                best = { outer, holes };
            }
        }
        return (best && best.outer.length >= 3) ? best : null;
    } catch (error) {
        console.error('PolygonOps.unionRingsToPolygon failed:', error);
        return null;
    }
}

/**
 * Union a list of polygons-with-holes into the minimal set of disjoint
 * polygons-with-holes. This is what merges every stroke and leg on a sketch
 * plane into ONE cutting outline per connected region (panels overlap in
 * the drawing but must be fabricated as a single piece).
 *
 * Robustness (martinez has repeatedly hung/scrambled on degenerate input):
 * - inputs get the deterministic sub-micron jitter used by
 *   unionRingsToPolygon to break exact tangencies/collinear overlaps
 * - rings are cleaned via ringToCoords (NaN + consecutive duplicates)
 * - operands are folded PAIRWISE - a self-intersecting multipolygon
 *   operand is ill-defined for martinez
 * - on ANY failure the un-unioned inputs are returned so geometry is
 *   never lost
 *
 * @param {Array<{outer: Array<{x,y}>, holes?: Array<Array<{x,y}>>}>} polygons
 * @returns {Array<{outer: Array<{x,y}>, holes: Array<Array<{x,y}>>}>}
 *          Disjoint union polygons (or the cleaned inputs on failure)
 */
export function unionShapes(polygons) {
    const valid = (polygons || [])
        .filter(p => p && p.outer && p.outer.length >= 3)
        .map(p => ({
            outer: p.outer,
            holes: (p.holes || []).filter(h => h && h.length >= 3)
        }));

    if (valid.length === 0) return [];
    if (valid.length === 1) return valid; // nothing to merge - keep exact input

    // Same deterministic sub-micron jitter as unionRingsToPolygon (see the
    // comment there): breaks exact tangencies without visible movement.
    let seed = 2;
    const jitter = () => {
        seed = (seed * 1664525 + 1013904223) >>> 0;
        return ((seed / 4294967296) - 0.5) * 2e-4;
    };
    const jitterRing = (ring) => ring.map(pt => ({
        x: pt.x + jitter(),
        y: pt.y + jitter()
    }));

    try {
        // Martinez polygon format: [outerCoords, holeCoords, ...]
        const toMartinez = (poly) => {
            const rings = [ringToCoords(jitterRing(poly.outer))];
            for (const hole of poly.holes) {
                const coords = ringToCoords(jitterRing(hole));
                if (coords.length >= 4) rings.push(coords);
            }
            return rings;
        };

        const first = toMartinez(valid[0]);
        if (first[0].length < 4) return valid;

        let result = [first]; // MultiPolygon
        for (let i = 1; i < valid.length; i++) {
            const operand = toMartinez(valid[i]);
            if (operand[0].length < 4) continue;
            const merged = martinez.union(result, [operand]);
            if (!merged || merged.length === 0) return valid;
            result = merged;
        }

        const out = [];
        for (const poly of result) {
            if (!poly || poly.length === 0) continue;
            const outer = coordsToRing(poly[0]);
            if (outer.length < 3) continue;
            const holes = [];
            for (let h = 1; h < poly.length; h++) {
                const hole = coordsToRing(poly[h]);
                if (hole.length >= 3) holes.push(hole);
            }
            out.push({ outer, holes });
        }

        if (out.length === 0) return valid;

        // Silent-geometry-loss guard: a union can never be smaller than its
        // largest operand. martinez occasionally returns a degenerate result
        // (without throwing) on hostile input - if the output area collapsed
        // below half the largest input, treat the whole fold as a failure
        // and keep the un-unioned shapes (never lose geometry silently).
        const polyArea = (p) => Math.abs(signedArea(p.outer)) -
            (p.holes || []).reduce((a, h) => a + Math.abs(signedArea(h)), 0);
        const outArea = out.reduce((a, p) => a + polyArea(p), 0);
        const maxInputArea = Math.max(...valid.map(p => Math.abs(signedArea(p.outer))));
        if (outArea < 0.5 * maxInputArea) {
            console.warn(`PolygonOps.unionShapes: result area ${outArea.toFixed(1)} < 50% of largest input ${maxInputArea.toFixed(1)}, keeping separate shapes`);
            return valid;
        }

        return out;
    } catch (error) {
        console.error('PolygonOps.unionShapes failed, keeping separate shapes:', error);
        return valid;
    }
}

/**
 * Backwards-compatible wrapper around unionRingsToPolygon returning only
 * the largest outer ring (holes discarded - do not use for shapes that can
 * legitimately enclose holes, e.g. closed brush strokes).
 * @param {Array<Array<{x,y}>>} rings - Simple open rings
 * @returns {Array<{x,y}>|null} Largest union outer ring, or null on failure
 */
export function unionRings(rings) {
    const polygon = unionRingsToPolygon(rings);
    return polygon ? polygon.outer : null;
}

/**
 * Subtract cutout rings (slots) from an outline ring.
 *
 * Cutouts fully inside the outline become holes; cutouts crossing the
 * boundary become notches (they reshape the outer ring). The result can
 * be multiple disjoint polygons if the cuts split the part.
 *
 * @param {Array<{x,y}>} outlineRing - Part outline (open ring)
 * @param {Array<Array<{x,y}>>} cutoutRings - Slot rectangles (open rings)
 * @param {Array<Array<{x,y}>>} [subjectHoles] - Holes already present in the
 *        part outline (e.g. the inner ring of a closed brush stroke); they
 *        are carried through the subtraction (and through every fallback)
 * @returns {Array<{outer: Array<{x,y}>, holes: Array<Array<{x,y}>>}>}
 *          Result polygons. Falls back to the uncut outline on failure.
 */
export function subtractCutouts(outlineRing, cutoutRings, subjectHoles = [], alreadyRetried = false) {
    const validHoles = (subjectHoles || []).filter(r => r && r.length >= 3);
    const fallback = [{ outer: outlineRing, holes: validHoles }];

    if (!outlineRing || outlineRing.length < 3) {
        return fallback;
    }
    const validCuts = (cutoutRings || []).filter(r => r && r.length >= 3);
    if (validCuts.length === 0) {
        return fallback;
    }

    // Same degeneracy guard as unionRings: coincident/tangent cut rectangles
    // (e.g. two slice groups placing slots at identical positions) can hang
    // martinez. Sub-micron deterministic jitter breaks exact coincidence.
    let seed = 1;
    const jitter = () => {
        seed = (seed * 1664525 + 1013904223) >>> 0;
        return ((seed / 4294967296) - 0.5) * 2e-4;
    };
    const jitteredCuts = validCuts.map(ring => ring.map(pt => ({
        x: pt.x + jitter(),
        y: pt.y + jitter()
    })));

    try {
        // Polygon: outer ring plus any pre-existing holes
        const subject = [ringToCoords(outlineRing), ...validHoles.map(ringToCoords)];

        // Cut rectangles can touch or overlap each other (e.g. slots of
        // adjacent panels only a material-thickness apart). A multipolygon
        // with overlapping members is ill-defined for martinez and can come
        // back as a scrambled self-intersecting ring - fold the cuts into a
        // proper union first, then subtract once.
        let clipping = [[ringToCoords(jitteredCuts[0])]];
        for (let i = 1; i < jitteredCuts.length; i++) {
            const merged = martinez.union(clipping, [[ringToCoords(jitteredCuts[i])]]);
            if (merged && merged.length > 0) clipping = merged;
        }

        const result = martinez.diff(subject, clipping);

        if (!result || result.length === 0) {
            // Cuts consumed the whole part - report and keep the uncut outline
            console.warn('PolygonOps.subtractCutouts: subtraction removed entire outline, keeping uncut part');
            return fallback;
        }

        // martinez returns MultiPolygon: [ [outerCoords, holeCoords...], ... ]
        const polygons = [];
        for (const poly of result) {
            if (!poly || poly.length === 0) continue;
            const outer = coordsToRing(poly[0]);
            if (outer.length < 3) continue;
            const holes = [];
            for (let i = 1; i < poly.length; i++) {
                const hole = coordsToRing(poly[i]);
                if (hole.length >= 3) holes.push(hole);
            }
            polygons.push({ outer, holes });
        }

        if (polygons.length === 0) return fallback;

        // Last-resort guard: martinez can still emit a self-intersecting
        // ring on vertex-on-edge degeneracies (slot corner exactly on the
        // outline). Retry once with 20x jitter before giving up.
        if (!alreadyRetried && polygons.some(poly => ringSelfIntersects(poly.outer))) {
            console.warn('PolygonOps.subtractCutouts: degenerate result, retrying with larger jitter');
            const bigger = validCuts.map(ring => ring.map(pt => ({
                x: pt.x + jitter() * 20,
                y: pt.y + jitter() * 20
            })));
            return subtractCutouts(outlineRing, bigger, subjectHoles, true);
        }

        return polygons;
    } catch (error) {
        console.error('PolygonOps.subtractCutouts failed, keeping uncut outline:', error);
        return fallback;
    }
}
